import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Paperclip, X, FileText, Image as ImageIcon, File, Mic, MicOff, Lock, Loader2, HelpCircle, Settings, ExternalLink, ScanText, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { navigateToPremium } from '../utils/navigateToPremium';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config/api';
import { getToken } from '../utils/authToken';
import { trackVoiceBlocked } from '../utils/trackVoice';
import { useLanguage } from '../context/LanguageContext';
import { useVoice } from '../context/VoiceContext';
import { useUserLimits } from '../state/userLimits';

const ALLOWED_EXTENSIONS = [
  '.txt', '.js', '.jsx', '.ts', '.tsx', '.py', '.html', '.css', '.json',
  '.md', '.yaml', '.yml', '.xml', '.sql', '.sh', '.java', '.cpp',
  '.c', '.go', '.rs', '.php', '.rb', '.swift', '.kt', '.csv',
  '.pdf', '.docx',
  '.png', '.jpg', '.jpeg', '.webp', '.gif'
];

const ChatInput = ({ 
  onSendMessage, 
  onSendWithFile, 
  onSendImageForVision,
  onReadImageWithVision,
  isLoading, 
  conversationId, 
  onImageGenerated, 
  onAnalyzeGeneratedImage
}) => {
  const { t, language } = useLanguage();
  const { setVoiceActive } = useVoice();
  const userLimits = useUserLimits();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  const [showMicHelp, setShowMicHelp] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // Para imagen de Vision_Replication
  // 🟥 CUADRO NEGRO — Vision UI Interpreter (2026-02-08). Estado del
  // sub-flujo que envía la imagen a /api/vision para crear/modificar el
  // Builder. Se activa solo cuando el usuario tiene ≥1 proyecto y clic
  // el botón "Aplicar al proyecto". No interfiere con el flujo Vision
  // clásico (mode='read-vision' → GPT-5.4 describe la imagen).
  const [visionProjects, setVisionProjects] = useState([]);
  const [visionApplying, setVisionApplying] = useState(false);
  const [visionSelectorOpen, setVisionSelectorOpen] = useState(false);
  // Dismiss del banner de cuota (Free Tier). Persiste en sessionStorage:
  // si el usuario lo cierra, queda oculto durante esta sesion del navegador.
  // Cuando inicia nueva pestana / nueva sesion, sessionStorage esta vacio y
  // el banner reaparece automaticamente. Tambien se limpia tras logout/login
  // (auth-changed event abajo).
  const [quotaBannerDismissed, setQuotaBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('damyxia_quota_banner_dismissed') === '1';
    } catch {
      return false;
    }
  });
  // Voice gating: solo usuarios Premium pueden usar el microfono
  const [isPremium, setIsPremium] = useState(null); // null = aun no se sabe
  const [showVoiceTooltip, setShowVoiceTooltip] = useState(false);
  // Auto-stop tras N segundos sin habla (config admin). 0 = deshabilitado.
  const [autoStopSeconds, setAutoStopSeconds] = useState(0);
  const autoStopSecondsRef = useRef(0);
  const autoStopTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null); // Ref separado para imágenes de Vision
  const visionReadInputRef = useRef(null); // Ref separado para "Leer imagen" (GPT-4o vision_mode)
  const recognitionRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // ─── Voz CONTINUA: refs para evitar closures stale en onresult/onend/onerror ───
  // El recognition se crea UNA sola vez (no cada vez que cambia isListening) y
  // los handlers leen el estado actual via ref. Sin esto, el `onend` reinicia
  // (o no) con un closure capturado en el momento del mount.
  const isListeningRef = useRef(false);
  const isSendingRef = useRef(false);          // evita doble-envio si el usuario habla y aprieta Send
  const messageRef = useRef('');               // ultimo valor del input (sin esperar al re-render)
  const autoSendTimerRef = useRef(null);       // timer "pause detector" para auto-send
  const onSendMessageRef = useRef(null);       // ref al callback onSendMessage (siempre fresco)

  const AUTO_SEND_PAUSE_MS = 1500; // tiempo de silencio tras finalTranscript que dispara el auto-envio
  
  const isSpanish = language === 'es';

  // Detectar si el usuario es Premium para gating del microfono
  useEffect(() => {
    const fetchSubscription = async () => {
      const token = getToken();
      if (!token || token === 'guest') {
        setIsPremium(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/subscriptions/status`, {
        });
        setIsPremium(!!response.data?.is_premium);
      } catch {
        setIsPremium(false);
      }
    };
    fetchSubscription();
  }, []);

  // Cargar config publica de voz (auto-stop tras inactividad)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/voice/config`)
      .then((res) => {
        if (cancelled) return;
        const v = Number(res.data?.auto_stop_seconds) || 0;
        setAutoStopSeconds(v);
        autoStopSecondsRef.current = v;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Mantener refs sincronizados con state para uso en los handlers de
  // recognition (que tienen closures capturados en el mount).
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);
  useEffect(() => { messageRef.current = message; }, [message]);
  useEffect(() => { onSendMessageRef.current = onSendMessage; }, [onSendMessage]);

  // Reset del dismiss-banner al cambiar de cuenta (logout/login). Garantiza
  // que un usuario nuevo vuelva a ver el banner aunque el usuario anterior
  // lo haya cerrado en la misma pestana.
  useEffect(() => {
    const onAuthChanged = () => {
      try { sessionStorage.removeItem('damyxia_quota_banner_dismissed'); } catch { /* noop */ }
      setQuotaBannerDismissed(false);
    };
    window.addEventListener('damyxia:auth-changed', onAuthChanged);
    return () => window.removeEventListener('damyxia:auth-changed', onAuthChanged);
  }, []);

  // Reinicia el timer de auto-stop. Llamado al iniciar el mic y en cada palabra detectada.
  // Lee desde ref para evitar closures stale en recognition.onresult.
  const resetAutoStopTimer = () => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    const seconds = autoStopSecondsRef.current;
    if (!seconds || seconds <= 0) return;
    autoStopTimerRef.current = setTimeout(() => {
      try {
        if (recognitionRef.current) recognitionRef.current.stop();
      } catch { /* ignore */ }
      try {
        const stream = mediaStreamRef?.current;
        if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      } catch { /* ignore */ }
      setIsListening(false);
      toast.info(isSpanish ? 'Voz detenida por inactividad' : 'Voice stopped due to inactivity');
    }, seconds * 1000);
  };

  // Helper: bloquea acciones de voz y muestra modal global "Funcion Premium"
  const blockVoiceForFree = () => {
    trackVoiceBlocked({ location: 'chat', language });
    try {
      window.dispatchEvent(
        new CustomEvent('damyxia:funcion-premium', {
          detail: { language, variant: 'voice' },
        })
      );
    } catch { /* ignore */ }
  };

  // Check microphone permission on mount
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(result.state);
          
          // Listen for permission changes
          result.onchange = () => {
            setMicPermission(result.state);
            if (result.state === 'granted') {
              toast.success(isSpanish ? '🎤 ¡Micrófono habilitado!' : '🎤 Microphone enabled!');
            }
          };
        }
      } catch (e) {
        // Permission API not supported, we'll check when user clicks
      }
    };
    
    checkMicPermission();
  }, [isSpanish]);

  // Inicializa el SpeechRecognition UNA sola vez. Los handlers leen el
  // estado actual via refs (isListeningRef, messageRef, onSendMessageRef)
  // para garantizar comportamiento continuo sin closures stale.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    setSpeechSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'es' ? 'es-ES' : 'en-US';

    // Pause detector: tras un finalTranscript, esperar AUTO_SEND_PAUSE_MS
    // de silencio antes de auto-enviar el mensaje. Si el usuario sigue hablando
    // (otro finalTranscript llega), el timer se reinicia y se acumula el texto.
    const scheduleAutoSend = () => {
      if (autoSendTimerRef.current) clearTimeout(autoSendTimerRef.current);
      autoSendTimerRef.current = setTimeout(() => {
        autoSendTimerRef.current = null;
        const text = (messageRef.current || '').trim();
        if (!text || isSendingRef.current) return;
        if (!isListeningRef.current) return; // mic ya apagado por el usuario
        try {
          isSendingRef.current = true;
          if (onSendMessageRef.current) onSendMessageRef.current(text);
          setMessage('');
          messageRef.current = '';
        } finally {
          // soltar el flag rápido para permitir el siguiente turno
          setTimeout(() => { isSendingRef.current = false; }, 250);
        }
      }, AUTO_SEND_PAUSE_MS);
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += transcript;
      }
      if (finalTranscript) {
        setMessage(prev => {
          const next = prev + finalTranscript + ' ';
          messageRef.current = next;
          return next;
        });
        // Solo programamos auto-send tras un final confirmado (no en interim).
        scheduleAutoSend();
      }
      // Cualquier resultado (final o intermedio) reinicia el timer de auto-stop.
      resetAutoStopTimer();
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      // Errores benignos en la mayoria de browsers (especialmente iOS Safari):
      // 'no-speech' aparece tras una pausa larga, 'aborted' cuando reinicia.
      // No apagamos el mic en esos casos — el `onend` se encargara del restart.
      if (event.error === 'not-allowed') {
        setMicPermission('denied');
        setShowMicHelp(true);
        setIsListening(false);
        isListeningRef.current = false;
      } else if (event.error === 'audio-capture') {
        toast.error(language === 'es' ? 'No se pudo acceder al microfono' : 'Could not access microphone');
        setIsListening(false);
        isListeningRef.current = false;
      }
      // 'no-speech', 'aborted', 'network': dejamos que onend maneje el restart.
    };

    // El navegador detiene `recognition` por varias razones (silencio, error,
    // mobile session timeout). Si el usuario aun queria seguir escuchando
    // (`isListeningRef.current === true`), reiniciamos automaticamente.
    // En iOS Safari esto ocurre tras CADA utterance — sin este restart, el
    // mic queda "activo visualmente" pero sin captura, que es exactamente
    // el bug reportado.
    recognition.onend = () => {
      if (!isListeningRef.current) return;
      // pequeño delay ayuda en iOS Safari y evita InvalidStateError si el
      // engine aun no termino de soltar el slot.
      setTimeout(() => {
        if (!isListeningRef.current) return;
        try {
          recognition.start();
        } catch (e) {
          // Si lanza InvalidStateError (ya esta corriendo), no hacemos nada.
          // Cualquier otro error apaga el mic para que el usuario lo reactive.
          if (e && e.name && e.name !== 'InvalidStateError') {
            console.warn('Recognition restart failed:', e);
            setIsListening(false);
            isListeningRef.current = false;
          }
        }
      }, 250);
    };

    recognitionRef.current = recognition;

    return () => {
      try { recognition.stop(); } catch { /* ignore */ }
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current);
        autoSendTimerRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
    };
    // IMPORTANTE: `language` es la unica dependencia razonable. Si cambia el
    // idioma re-creamos el recognition con la nueva config. NO incluimos
    // `isListening` porque eso destruiria/recrearia el recognition en cada
    // toggle, rompiendo el modo continuo (ese era el bug original).
  }, [language]);

  // Update recognition language when language changes
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language === 'es' ? 'es-ES' : 'en-US';
    }
  }, [language]);

  // Sincronizar isListening con VoiceContext (estado global cross-page)
  useEffect(() => {
    setVoiceActive(isListening);
  }, [isListening, setVoiceActive]);

  // Escuchar peticion global de detener voz (boton en HOME / VoiceMicButton)
  useEffect(() => {
    const handleStop = () => {
      if (isListening) stopMicrophone();
    };
    window.addEventListener('damyxia:stop-voice', handleStop);
    return () => window.removeEventListener('damyxia:stop-voice', handleStop);
  }, [isListening]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-activacion del microfono cuando viene de ?activate-voice=1
  // (ej. usuario Premium hizo click en el boton mic del HOME).
  // iOS Safari requiere gesto del usuario, asi que solo lanzamos un toast
  // que invita a tocar el boton mic - no auto-activamos sin interaccion.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('activate-voice') !== '1') return;
    // Limpiar el query param para que no se quede pegado.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('activate-voice');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
    if (isPremium === false || !speechSupported) return;
    // Mostrar hint visible sin auto-activar (compat iOS Safari).
    toast.message(
      isSpanish ? 'Toca el ícono del micrófono para hablar' : 'Tap the mic icon to start talking',
      { duration: 3500 }
    );
  }, [location.search, isPremium, speechSupported, isSpanish]);

  // Auto-activate microphone when user types "micrófono" or "microfono"
  useEffect(() => {
    const lowerMessage = message.toLowerCase().trim();
    const micKeywords = ['micrófono', 'microfono', 'microphone', 'mic'];
    
    if (micKeywords.some(kw => lowerMessage === kw) && !isListening && speechSupported) {
      setMessage('');
      // Voice gating: Free no puede activar el microfono ni siquiera por keyword
      if (isPremium === false) {
        blockVoiceForFree();
        return;
      }
      activateMicrophone();
    }
  }, [message, isListening, speechSupported, isPremium]); // eslint-disable-line react-hooks/exhaustive-deps

  // Activate microphone with permission handling.
  // Setea isListeningRef.current=true ANTES del recognition.start() para
  // que onend pueda reiniciar el recognition automaticamente.
  //
  // ⚠️ MOBILE FIX (Android Chrome / iOS Safari):
  // Antes manteniamos el `MediaStream` abierto durante toda la sesion para
  // mostrar un "indicador verde" en la barra del browser. Pero en mobile esto
  // BLOQUEA al motor de Web Speech (solo un consumidor por mic) → el mic
  // queda visualmente activo pero no captura voz.
  //
  // Ahora pedimos getUserMedia SOLO para disparar el prompt de permisos,
  // e INMEDIATAMENTE liberamos los tracks. Web Speech captura su propio
  // audio sin colision. El permiso queda cacheado en el browser.
  const activateMicrophone = async () => {
    try {
      // Pedir permiso. Liberar el stream apenas lo tenemos.
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicPermission('granted');
      } finally {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        // Aseguramos que NO haya un stream colgando del refrenviado anterior.
        if (mediaStreamRef.current) {
          try { mediaStreamRef.current.getTracks().forEach(t => t.stop()); } catch (_) { /* noop */ }
          mediaStreamRef.current = null;
        }
      }

      // Start speech recognition (captura su propio audio del SO).
      if (recognitionRef.current) {
        isListeningRef.current = true;
        try {
          recognitionRef.current.start();
        } catch (e) {
          // Si ya esta corriendo (InvalidStateError), no hacemos nada — el flujo
          // continua. Cualquier otro error apaga el mic.
          if (e && e.name && e.name !== 'InvalidStateError') {
            isListeningRef.current = false;
            throw e;
          }
        }
        setIsListening(true);
        resetAutoStopTimer();
        toast.success(
          isSpanish
            ? '🎤 Escuchando — habla cuando quieras. El micrófono se queda activo hasta que lo apagues.'
            : '🎤 Listening — speak whenever. The mic stays on until you turn it off.'
        );
      }
    } catch (err) {
      console.error('Microphone permission error:', err);
      setMicPermission('denied');

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setShowMicHelp(true);
      } else if (err.name === 'NotFoundError') {
        toast.error(
          isSpanish
            ? '🎤 No se encontró micrófono. Conecta un micrófono e intenta de nuevo.'
            : '🎤 No microphone found. Please connect a microphone and try again.'
        );
      } else {
        toast.error(
          isSpanish
            ? '🎤 Error al acceder al micrófono. Intenta de nuevo.'
            : '🎤 Error accessing microphone. Please try again.'
        );
      }
    }
  };

  // Stop microphone and release resources.
  // Importante: setea isListeningRef.current=false ANTES de recognition.stop()
  // para que el handler `onend` no reinicie el recognition.
  const stopMicrophone = () => {
    isListeningRef.current = false;
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (autoSendTimerRef.current) {
      clearTimeout(autoSendTimerRef.current);
      autoSendTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setIsListening(false);
    toast.info(isSpanish ? 'Micrófono desactivado' : 'Microphone off');
  };

  const toggleListening = async () => {
    if (!speechSupported) {
      toast.error(isSpanish ? 'Tu navegador no soporta reconocimiento de voz' : 'Your browser does not support speech recognition');
      return;
    }
    // Voice gating: solo Premium puede activar el microfono
    if (isPremium === false) {
      blockVoiceForFree();
      return;
    }

    if (isListening) {
      stopMicrophone();
    } else {
      await activateMicrophone();
    }
  };

  const handleSendGeneratedImageToChat = (imageData) => {
    if (onAnalyzeGeneratedImage && imageData.base64) {
      onAnalyzeGeneratedImage(imageData);
    }
  };

  // Handler para subir imagen — 🟥 CUADRO NEGRO 2026-06-30 (Opción A):
  // ahora activa Vision automáticamente (GPT-5.4) en vez del antiguo
  // flujo Vision_Replication. Esto arregla el bug "no puedo ver imágenes":
  // adjuntar una imagen ahora envía vision_mode=true + image_base64 al
  // /api/chat, para que DamyxIA la lea e interprete de verdad.
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      toast.error(isSpanish ? 'Por favor selecciona una imagen válida' : 'Please select a valid image');
      return;
    }

    // Validar tamaño (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isSpanish ? 'La imagen es demasiado grande (máx. 10MB)' : 'Image is too large (max. 10MB)');
      return;
    }

    // Convertir a base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;

      // Guardar imagen pendiente para mostrar preview.
      // mode='read-vision' → handleSendImageWithMessage lo rutea a
      // onReadImageWithVision (POST /api/chat con vision_mode=true).
      setPendingImage({
        base64,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        mode: 'read-vision',
      });
    };

    reader.readAsDataURL(file);
    
    // Limpiar input para permitir subir la misma imagen de nuevo
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Handler para subir imagen al modo "Leer imagen" (GPT-4o vision_mode true).
  // Idéntico al handler de Vision_Replication pero marca el modo como
  // 'read-vision' para que handleSendImageWithMessage rute al endpoint correcto.
  const handleVisionReadUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(isSpanish ? 'Por favor selecciona una imagen válida' : 'Please select a valid image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isSpanish ? 'La imagen es demasiado grande (máx. 10MB)' : 'Image is too large (max. 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingImage({
        base64: reader.result,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        mode: 'read-vision'
      });
    };
    reader.readAsDataURL(file);

    if (visionReadInputRef.current) {
      visionReadInputRef.current.value = '';
    }
  };

  // Enviar imagen con mensaje opcional
  const handleSendImageWithMessage = () => {
    if (!pendingImage) return;

    // Modo "Leer imagen" (GPT-4o vision_mode): nuevo flujo a /api/chat
    if (pendingImage.mode === 'read-vision' && onReadImageWithVision) {
      const base64Pure = typeof pendingImage.base64 === 'string' && pendingImage.base64.includes(',')
        ? pendingImage.base64.split(',')[1]
        : pendingImage.base64;
      onReadImageWithVision({
        imageBase64: base64Pure,
        mimeType: pendingImage.mimeType || 'image/png',
        filename: pendingImage.filename,
        message: message.trim() || (isSpanish
          ? 'Lee y transcribe el texto de la imagen.'
          : 'Read and transcribe the text in the image.')
      });
      setPendingImage(null);
      setMessage('');
      return;
    }

    // Modo Vision_Replication (flujo original sin tocar)
    if (onSendImageForVision) {
      onSendImageForVision({
        type: 'image',
        content: pendingImage.base64,
        filename: pendingImage.filename,
        instruction: message.trim() || ''
      });
    }
    
    // Limpiar estado
    setPendingImage(null);
    setMessage('');
  };

  // Cancelar imagen pendiente
  const removePendingImage = () => {
    setPendingImage(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // 🟥 CUADRO NEGRO 2026-06-30 (drag & drop imágenes sobre textarea) —
  // helper para procesar un File suelto (drag drop o paste) reutilizando
  // exactamente el flujo del botón 🖼️: mode='read-vision' para que al
  // enviar dispare vision_mode=true + image_base64 a /api/chat (GPT-5.4).
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const dragCounterRef = useRef(0);
  const ingestDroppedImage = useCallback((file) => {
    if (!file) return;
    if (isPremium === false) {
      toast.error(isSpanish
        ? 'Leer imágenes es exclusivo Premium. Actualiza tu plan para activarlo.'
        : 'Reading images is Premium-only. Upgrade to enable it.',
        {
          duration: 6000,
          action: {
            label: isSpanish ? 'Actualizar' : 'Upgrade',
            onClick: navigateToPremium,
          },
        });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(isSpanish ? 'Solo se aceptan imágenes' : 'Only images are accepted');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isSpanish ? 'La imagen es demasiado grande (máx. 10MB)' : 'Image is too large (max. 10MB)');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingImage({
        base64: reader.result,
        filename: file.name || 'dropped-image.png',
        size: file.size,
        mimeType: file.type,
        mode: 'read-vision',
      });
    };
    reader.readAsDataURL(file);
  }, [isPremium, isSpanish, navigateToPremium]);

  const handleTextareaDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) {
      dragCounterRef.current += 1;
      setIsDraggingImage(true);
    }
  };
  const handleTextareaDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  const handleTextareaDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingImage(false);
  };
  const handleTextareaDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingImage(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) ingestDroppedImage(file);
  };

  // 🟥 CUADRO NEGRO 2026-06-30 (paste imágenes) — Ctrl+V / Cmd+V / clic
  // derecho → Pegar. Si el portapapeles contiene una imagen, la ingesta
  // por el mismo flujo read-vision (GPT-5.4). Si no hay imagen, deja
  // que el paste de texto siga su curso normal.
  const handleTextareaPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          ingestDroppedImage(file);
          return;
        }
      }
    }
  };

  // 🟥 CUADRO NEGRO — Vision_UI_Interpreter (2026-02-08).
  // Al abrir el selector, carga la lista de proyectos del Builder del
  // usuario. Endpoint aislado /api/builder/projects; no llama /api/chat.
  const openVisionSelector = useCallback(async () => {
    setVisionSelectorOpen(true);
    if (visionProjects.length > 0) return;
    try {
      const { data } = await axios.get(`${API_URL}/builder/projects`, { withCredentials: true });
      setVisionProjects(data.projects || []);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 403) {
        toast.error(isSpanish ? 'Solo admin/creator puede usar Vision UI' : 'Admin/creator only');
      } else {
        toast.error(isSpanish ? 'No se pudo cargar tus proyectos' : 'Could not load projects');
      }
      setVisionSelectorOpen(false);
    }
  }, [visionProjects.length, isSpanish]);

  // Convierte una data-URL (base64) a File para poder mandarlo por multipart.
  const dataUrlToFile = (dataUrl, filename, mimeType) => {
    const src = typeof dataUrl === 'string' && dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
    const bin = atob(src);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], filename || 'chat-image.png', { type: mimeType || 'image/png' });
  };

  // Dispara upload + interpret contra /api/vision/*. NO toca /api/chat.
  const applyImageToProject = useCallback(async (projectId, projectName) => {
    if (!pendingImage) return;
    if (!projectId) return;
    setVisionApplying(true);
    setVisionSelectorOpen(false);
    try {
      const file = dataUrlToFile(pendingImage.base64, pendingImage.filename, pendingImage.mimeType || 'image/png');
      const form = new FormData();
      form.append('file', file);
      const upResp = await axios.post(
        `${API_URL}/vision/upload?project_id=${encodeURIComponent(projectId)}`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const imageId = upResp.data?.image_id;
      if (!imageId) throw new Error('image_id ausente');
      const command = (message && message.trim()) || 'usar esta imagen para replicar la UI';
      const { data } = await axios.post(
        `${API_URL}/vision/interpret`,
        { image_id: imageId, project_id: projectId, user_command: command, apply: true },
        { withCredentials: true }
      );
      toast.success(
        isSpanish
          ? `Aplicado a "${projectName}" · ${data.applied} acciones`
          : `Applied to "${projectName}" · ${data.applied} actions`,
        {
          duration: 6000,
          action: {
            label: isSpanish ? 'Ver' : 'Open',
            onClick: () => { window.location.href = `/admin/builder`; },
          },
        }
      );
      // Limpia el pending image ya que se consumió por Vision UI.
      setPendingImage(null);
      setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isSpanish ? 'Error al aplicar al proyecto' : 'Error applying to project'));
    } finally {
      setVisionApplying(false);
    }
  }, [pendingImage, message, isSpanish]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    // Bloqueo oficial — Plan Gratis sin cupo (regla 2.1) o trial agotado.
    // Premium (mode='premium') y trial activo (mode='trial') siempre pueden
    // enviar; modo 'blocked' = post-trial sin cupo diario.
    if (!userLimits.is_premium && userLimits.isMessageBlocked) {
      const isEs = language === 'es';
      toast.error(
        isEs
          ? 'Has alcanzado tu limite de 6 mensajes hoy. Actualiza a Premium para mensajes ilimitados.'
          : "You've used your 6 messages today. Upgrade to Premium for unlimited messages.",
        {
          duration: 7000,
          action: {
            label: isEs ? 'Actualizar' : 'Upgrade',
            onClick: navigateToPremium,
          },
        }
      );
      return;
    }

    // Si hay auto-send pendiente desde el reconocimiento de voz, lo cancelamos
    // para evitar doble-envio.
    if (autoSendTimerRef.current) {
      clearTimeout(autoSendTimerRef.current);
      autoSendTimerRef.current = null;
    }

    // Si hay imagen pendiente para Vision_Replication
    if (pendingImage) {
      handleSendImageWithMessage();
      return;
    }

    if (selectedFile) {
      if (onSendWithFile) {
        onSendWithFile(message || t('chat.analyzeFile'), selectedFile);
      }
      setSelectedFile(null);
      setFilePreview(null);
      setMessage('');
      messageRef.current = '';
    } else if (message.trim()) {
      onSendMessage(message);
      setMessage('');
      messageRef.current = '';
    }
    // Refresca el contador del Free Tier tras enviar (el backend ya descontó):
    // permite que el banner muestre la disminucion en tiempo real sin esperar
    // a un focus event. Es no-op si el usuario es Premium (el endpoint
    // responde igual rapido) y best-effort si falla.
    if (userLimits && typeof userLimits.refresh === 'function') {
      // Pequeno retraso para que el backend complete la transaccion.
      setTimeout(() => { try { userLimits.refresh(); } catch { /* noop */ } }, 600);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      alert(`${t('chat.fileTypeNotSupported')} ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert(t('chat.fileTooLarge'));
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
      return <ImageIcon className="w-4 h-4" />;
    }
    if (['pdf', 'docx'].includes(ext)) {
      return <FileText className="w-4 h-4" />;
    }
    return <File className="w-4 h-4" />;
  };

  // Microphone Help Modal Component - Compact version
  const MicrophoneHelpModal = () => {
    if (!showMicHelp) return null;
    
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        onClick={() => setShowMicHelp(false)}
      >
        <div 
          className="bg-zinc-900 light:bg-white border border-zinc-700 light:border-zinc-300 rounded-xl p-4 max-w-xs mx-4 shadow-xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => setShowMicHelp(false)}
            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-zinc-800 hover:bg-zinc-700 light:bg-zinc-200 light:hover:bg-zinc-300 text-zinc-400 hover:text-white light:text-zinc-600 light:hover:text-zinc-900 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 pr-6">
            <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <MicOff className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-sm font-medium text-zinc-200 light:text-zinc-800">
              {isSpanish ? 'Permiso de micrófono denegado' : 'Microphone permission denied'}
            </p>
          </div>
          
          {/* Instructions */}
          <p className="text-xs text-zinc-400 light:text-zinc-600 mb-3">
            {isSpanish 
              ? '🔒 Haz clic en el candado de la barra de direcciones → Micrófono → Permitir → Recarga (F5)' 
              : '🔒 Click lock icon in address bar → Microphone → Allow → Reload (F5)'}
          </p>
          
          {/* Reload button */}
          <button
            onClick={() => window.location.reload()}
            className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {isSpanish ? 'Recargar página' : 'Reload page'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="chat-composer"
      className="pt-2 px-2 pb-2 sm:p-6 bg-zinc-950/80 light:bg-white/80 backdrop-blur-xl border-t border-zinc-800/50 light:border-zinc-200 transition-colors duration-300"
    >
      {/* Banner de limite de mensajes (regla oficial — free_tier).
          Visibilidad ESTRICTA:
            - SOLO no-Premium (chequea is_premium Y mode!=='premium').
            - Solo si esta en trial / daily / blocked.
            - El usuario puede cerrarlo con X — se persiste en sessionStorage,
              asi que reaparece automaticamente al iniciar nueva pestana o
              tras logout/login.
            - El contador disminuye en vivo (refresh tras cada send). */}
      {userLimits.plan_known
        && !userLimits.is_premium
        && userLimits.mode !== 'premium'
        && !userLimits.loading
        && !quotaBannerDismissed
        && (userLimits.isMessageBlocked || userLimits.mode === 'trial' || userLimits.mode === 'daily') && (
        <div
          data-testid="chatinput-quota-banner"
          className={`max-w-4xl mx-auto mb-2 px-3 py-1.5 rounded-md border text-xs flex items-center justify-between gap-2 ${
            userLimits.isMessageBlocked
              ? 'border-rose-500/40 bg-rose-950/40 text-rose-200'
              : userLimits.mode === 'trial'
                ? 'border-emerald-500/30 bg-emerald-950/30 text-emerald-200'
                : 'border-amber-500/30 bg-amber-950/30 text-amber-200'
          }`}
        >
          <div className="min-w-0 flex-1">
            {userLimits.isMessageBlocked ? (
              <span data-testid="chatinput-quota-blocked">
                {language === 'es'
                  ? 'Sin mensajes disponibles hoy. Actualiza a Premium para enviar mas.'
                  : 'No messages left today. Upgrade to Premium to send more.'}
              </span>
            ) : userLimits.mode === 'trial' ? (
              <span data-testid="chatinput-quota-trial">
                {language === 'es'
                  ? `Primer Mes Gratis — quedan ${userLimits.trial_messages_remaining} mensajes (de ${userLimits.trial_messages_max}).`
                  : `First Free Month — ${userLimits.trial_messages_remaining} messages left (out of ${userLimits.trial_messages_max}).`}
              </span>
            ) : (
              <span data-testid="chatinput-quota-daily">
                {language === 'es'
                  ? `Plan Gratis — quedan ${userLimits.daily_messages_remaining} mensajes hoy (de ${userLimits.daily_messages_max}).`
                  : `Free Plan — ${userLimits.daily_messages_remaining} messages left today (out of ${userLimits.daily_messages_max}).`}
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid="chatinput-quota-upgrade-btn"
            onClick={navigateToPremium}
            className="shrink-0 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 hover:from-emerald-300 hover:to-cyan-300 transition-colors"
          >
            {language === 'es' ? 'Actualizar' : 'Upgrade'}
          </button>
          <button
            type="button"
            data-testid="chatinput-quota-dismiss-btn"
            onClick={() => {
              try { sessionStorage.setItem('damyxia_quota_banner_dismissed', '1'); } catch { /* noop */ }
              setQuotaBannerDismissed(true);
            }}
            aria-label={language === 'es' ? 'Cerrar' : 'Close'}
            title={language === 'es' ? 'Cerrar' : 'Close'}
            className="shrink-0 p-1 -mr-1 rounded hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5 opacity-80" />
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        {/* Boton "Detener voz" visible cuando el mic esta activo */}
        {isListening && (
          <div className="mb-2 flex justify-center">
            <button
              type="button"
              onClick={stopMicrophone}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full
                         bg-rose-500 hover:bg-rose-400 text-white text-sm font-semibold
                         shadow-lg shadow-rose-500/40 animate-toc-fade-in
                         touch-manipulation"
              data-testid="stop-voice-chat-btn"
            >
              <span className="relative inline-flex w-2.5 h-2.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-white opacity-75 animate-ping" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-white" />
              </span>
              {isSpanish ? 'Detener voz' : 'Stop voice'}
            </button>
          </div>
        )}

        {/* File Preview */}
        {selectedFile && (
          <div className="mb-3 p-3 bg-zinc-900/50 light:bg-zinc-100 border border-zinc-800/50 light:border-zinc-200 rounded-xl">
            <div className="flex items-center gap-3">
              {filePreview ? (
                <img 
                  src={filePreview} 
                  alt="Preview" 
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-12 h-12 bg-indigo-600/20 rounded-lg flex items-center justify-center text-indigo-400">
                  {getFileIcon(selectedFile.name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-200 light:text-zinc-800 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={removeFile}
                className="p-1.5 text-zinc-500 hover:text-zinc-300 light:hover:text-zinc-700 transition-colors"
                data-testid="remove-file-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input - kept for programmatic file selection */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          accept={ALLOWED_EXTENSIONS.join(',')}
          className="hidden"
          data-testid="file-input"
        />

        {/* Hidden image input for Vision_Replication */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
          data-testid="image-upload-input"
        />

        {/* Hidden image input for "Leer imagen" (GPT-4o vision_mode true) */}
        <input
          ref={visionReadInputRef}
          type="file"
          accept="image/*"
          onChange={handleVisionReadUpload}
          className="hidden"
          data-testid="vision-read-input"
        />

        {/* Pending Image Preview for Vision_Replication or Vision Read */}
        {pendingImage && (
          <div className={`mb-3 p-3 ${pendingImage.mode === 'read-vision' ? 'bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/30' : 'bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-cyan-500/30'} border rounded-xl`}>
            <div className="flex items-center gap-3">
              <img 
                src={pendingImage.base64} 
                alt="Preview" 
                className={`w-16 h-16 object-cover rounded-lg border ${pendingImage.mode === 'read-vision' ? 'border-emerald-500/30' : 'border-cyan-500/30'}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${pendingImage.mode === 'read-vision' ? 'text-emerald-300' : 'text-cyan-300'}`}>
                  {pendingImage.filename}
                </p>
                <p className="text-xs text-zinc-500">
                  {(pendingImage.size / 1024).toFixed(1)} KB • {pendingImage.mode === 'read-vision' ? (isSpanish ? 'Leer imagen (GPT-5.4)' : 'Read image (GPT-5.4)') : 'Vision_Replication'}
                </p>
                <p className={`text-xs mt-1 ${pendingImage.mode === 'read-vision' ? 'text-emerald-400/70' : 'text-cyan-400/70'}`}>
                  {pendingImage.mode === 'read-vision'
                    ? (isSpanish ? 'Escribe que quieres saber o envia para transcribir' : 'Type what you want to know or send to transcribe')
                    : (isSpanish ? 'Escribe instrucciones o envía para analizar' : 'Type instructions or send to analyze')}
                </p>
              </div>
              {/* 🟥 Vision UI Interpreter — botón "Aplicar al proyecto".
                  Al hacer clic, abre selector de proyecto del Builder y
                  dispara /api/vision/upload + /api/vision/interpret. */}
              <button
                type="button"
                onClick={openVisionSelector}
                disabled={visionApplying}
                className="px-2.5 py-1.5 rounded-lg bg-fuchsia-600/20 hover:bg-fuchsia-600/40 border border-fuchsia-500/40 text-fuchsia-200 text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
                title={isSpanish ? 'Aplicar al proyecto del Builder (Vision UI)' : 'Apply to Builder project (Vision UI)'}
                data-testid="vision-apply-btn"
              >
                {visionApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {isSpanish ? 'Aplicar al proyecto' : 'Apply to project'}
              </button>
              <button
                type="button"
                onClick={removePendingImage}
                className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                data-testid="remove-pending-image-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Selector inline de proyecto del Builder */}
            {visionSelectorOpen && (
              <div
                className="mt-3 pt-3 border-t border-fuchsia-500/30"
                data-testid="vision-project-selector"
              >
                <div className="text-[11px] uppercase tracking-wide text-fuchsia-300 mb-2">
                  {isSpanish ? 'Elegí un proyecto del Builder' : 'Choose a Builder project'}
                </div>
                {visionProjects.length === 0 ? (
                  <div className="text-xs text-zinc-500">
                    {isSpanish
                      ? 'Sin proyectos. Creá uno en /admin/builder primero.'
                      : 'No projects. Create one at /admin/builder first.'}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {visionProjects.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => applyImageToProject(p.id, p.name)}
                        className="px-2.5 py-1 rounded-md bg-slate-800 hover:bg-fuchsia-600 border border-slate-700 text-xs text-white transition-colors"
                        data-testid={`vision-project-option-${p.id}`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setVisionSelectorOpen(false)}
                  className="mt-2 text-[11px] text-zinc-500 hover:text-white"
                >
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Text Input Area with Microphone and Image Upload */}
        <div className="flex items-end gap-2 flex-1">
          {/* Image Upload Button — 🟥 CUADRO NEGRO 2026-06-30 (Opción A):
              ahora activa Vision automáticamente (GPT-5.4) al enviar. */}
          <button
            type="button"
            onClick={() => {
              if (isPremium === false) {
                toast.error(isSpanish
                  ? 'Leer imágenes es exclusivo Premium. Actualiza tu plan para activarlo.'
                  : 'Reading images is Premium-only. Upgrade to enable it.',
                  {
                    duration: 6000,
                    action: {
                      label: isSpanish ? 'Actualizar' : 'Upgrade',
                      onClick: navigateToPremium,
                    },
                  });
                return;
              }
              imageInputRef.current?.click();
            }}
            disabled={isLoading || pendingImage}
            className="p-3 bg-zinc-900/50 light:bg-zinc-50 border border-zinc-800/50 light:border-zinc-200 hover:border-cyan-500/50 hover:bg-cyan-500/10 text-zinc-400 hover:text-cyan-400 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isSpanish ? 'Enviar imagen — DamyxIA la lee y describe' : 'Send image — DamyxIA reads and describes it'}
            data-testid="upload-image-btn"
          >
            <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
          </button>

          {/* Vision Read Button — GPT-4o vision_mode (exclusivo Premium) */}
          <button
            type="button"
            onClick={() => {
              if (isPremium === false) {
                toast.error(isSpanish
                  ? 'Leer imagen con GPT-4o es exclusivo Premium. Actualiza tu plan para activarlo.'
                  : 'Reading images with GPT-4o is Premium-only. Upgrade to enable it.',
                  {
                    duration: 6000,
                    action: {
                      label: isSpanish ? 'Actualizar' : 'Upgrade',
                      onClick: navigateToPremium,
                    },
                  });
                return;
              }
              visionReadInputRef.current?.click();
            }}
            disabled={isLoading || pendingImage}
            className={`p-3 bg-zinc-900/50 light:bg-zinc-50 border border-zinc-800/50 light:border-zinc-200 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              isPremium === false
                ? 'text-zinc-600 hover:text-amber-400 hover:border-amber-500/40 cursor-help'
                : 'hover:border-emerald-500/50 hover:bg-emerald-500/10 text-zinc-400 hover:text-emerald-400'
            }`}
            title={isPremium === false
              ? (isSpanish ? 'Premium — Leer imagen con GPT-4o' : 'Premium — Read image with GPT-4o')
              : (isSpanish ? 'Leer texto de imagen con GPT-4o' : 'Read text from image with GPT-4o')}
            data-testid="read-image-btn"
            data-premium-locked={isPremium === false ? 'true' : 'false'}
            aria-label={isSpanish ? 'Leer imagen' : 'Read image'}
          >
            {isPremium === false ? (
              <Lock className="w-5 h-5" strokeWidth={1.5} />
            ) : (
              <ScanText className="w-5 h-5" strokeWidth={1.5} />
            )}
          </button>

          <div
            className="flex-1 relative"
            onDragEnter={handleTextareaDragEnter}
            onDragOver={handleTextareaDragOver}
            onDragLeave={handleTextareaDragLeave}
            onDrop={handleTextareaDrop}
            onPaste={handleTextareaPaste}
          >
            <textarea
              data-testid="chat-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                selectedFile
                  ? t('chat.placeholderFile')
                  : isPremium === false
                    ? (isSpanish
                        ? '🖼️ Subir imágenes es Premium — Actualizá tu plan'
                        : '🖼️ Uploading images is Premium — Upgrade your plan')
                    : t('chat.placeholder')
              }
              disabled={isLoading}
              rows={1}
              className="w-full px-4 py-3 pr-12 bg-zinc-900/50 light:bg-zinc-50 border border-zinc-800/50 light:border-zinc-200 rounded-xl text-zinc-200 light:text-zinc-900 placeholder-zinc-600 light:placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 resize-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: '48px', maxHeight: '200px' }}
            />

            {/* 🟥 CUADRO NEGRO 2026-06-30 — Overlay visual durante drag&drop
                de imágenes sobre el textarea. Mismo flujo read-vision → GPT-5.4. */}
            {isDraggingImage && (
              <div
                className="absolute inset-0 rounded-xl border-2 border-dashed border-cyan-400 bg-cyan-500/15 backdrop-blur-[2px] flex items-center justify-center pointer-events-none z-10 animate-toc-fade-in"
                data-testid="textarea-drop-overlay"
              >
                <div className="flex items-center gap-2 text-cyan-300 font-medium text-sm">
                  <ImageIcon className="w-4 h-4" />
                  {isSpanish ? 'Soltá la imagen para que DamyxIA la lea' : 'Drop the image for DamyxIA to read'}
                </div>
              </div>
            )}
            
            {/* Microphone Button Inside Input */}
            {speechSupported && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {/* Tooltip exclusivo Premium para usuarios Free */}
                {showVoiceTooltip && isPremium === false && (
                  <div
                    className="absolute bottom-full right-0 mb-2 whitespace-nowrap
                               bg-zinc-900 text-white text-xs px-3 py-2 rounded-lg
                               shadow-xl border border-amber-500/40 animate-toc-fade-in
                               pointer-events-none z-[9999]"
                    data-testid="voice-premium-tooltip"
                  >
                    {isSpanish
                      ? 'Habla con DamyxIA — exclusivo en Premium'
                      : 'Talk to DamyxIA — Premium only'}
                  </div>
                )}
                <button
                  type="button"
                  onClick={
                    isPremium === false
                      ? blockVoiceForFree
                      : micPermission === 'denied'
                        ? () => setShowMicHelp(true)
                        : toggleListening
                  }
                  onMouseEnter={() => { if (isPremium === false) setShowVoiceTooltip(true); }}
                  onMouseLeave={() => setShowVoiceTooltip(false)}
                  onFocus={() => { if (isPremium === false) setShowVoiceTooltip(true); }}
                  onBlur={() => setShowVoiceTooltip(false)}
                  disabled={isLoading}
                  className={`w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                    isPremium === false
                      ? 'bg-amber-500/10 text-amber-300/80 hover:bg-amber-500/20 cursor-pointer'
                      : isListening
                        ? 'bg-red-500/90 text-white animate-pulse shadow-lg shadow-red-500/50 scale-110'
                        : micPermission === 'denied'
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-transparent hover:bg-zinc-700/50 light:hover:bg-zinc-200/50 text-zinc-400 light:text-zinc-500 hover:text-zinc-200 light:hover:text-zinc-700'
                  } disabled:opacity-50`}
                  title={
                    isPremium === false
                      ? (isSpanish ? 'Habla con DamyxIA — exclusivo en Premium' : 'Talk to DamyxIA — Premium only')
                      : isListening
                        ? (isSpanish ? 'Detener grabación' : 'Stop recording')
                        : micPermission === 'denied'
                          ? (isSpanish ? 'Permiso denegado - Haz clic para ayuda' : 'Permission denied - Click for help')
                          : (isSpanish ? 'Hablar con DamyxIA' : 'Talk to DamyxIA')
                  }
                  aria-label={
                    isPremium === false
                      ? (isSpanish ? 'Habla con DamyxIA — exclusivo en Premium' : 'Talk to DamyxIA — Premium only')
                      : (isSpanish ? 'Hablar con DamyxIA' : 'Talk to DamyxIA')
                  }
                  data-testid="voice-input-btn"
                >
                  {isPremium === false ? (
                    <Lock className="w-3.5 h-3.5" />
                  ) : isListening ? (
                    <MicOff className="w-3.5 h-3.5" />
                  ) : micPermission === 'denied' ? (
                    <HelpCircle className="w-3.5 h-3.5" />
                  ) : (
                    <Mic className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            )}
          </div>
          
          <button
            data-testid="send-message-btn"
            type="submit"
            disabled={
              (!message.trim() && !selectedFile && !pendingImage)
              || isLoading
              || (!userLimits.is_premium && userLimits.isMessageBlocked)
            }
            className={`p-3 ${pendingImage ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:bg-zinc-800 light:disabled:bg-zinc-200 disabled:text-zinc-600 light:disabled:text-zinc-400 text-white rounded-xl transition-colors duration-200 disabled:cursor-not-allowed`}
          >
            <Send className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <p className="mt-1 sm:mt-2 text-[10px] sm:text-xs text-zinc-600 light:text-zinc-500 text-center">
          {t('chat.supportedFormats')}
        </p>

        {/* 🟥 CUADRO NEGRO — Persistent Free-Tier Counter (P2.usage_ui).
            Chip siempre visible para usuarios de Plan Gratis (trial o daily).
            NUNCA se muestra a Premium. No es descartable (a diferencia del
            banner de alerta). Se apoya en useUserLimits() → mismo shape que
            /api/free-tier/status. */}
        {userLimits.plan_known
          && !userLimits.is_premium
          && userLimits.mode
          && userLimits.mode !== 'premium' && (
          <div className="mt-1 flex justify-center">
            <div
              data-testid="chatinput-free-tier-counter"
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-medium border ${
                userLimits.mode === 'blocked'
                  ? 'border-rose-500/40 bg-rose-950/30 text-rose-300'
                  : userLimits.mode === 'trial'
                    ? 'border-emerald-500/30 bg-emerald-950/20 text-emerald-300'
                    : userLimits.daily_messages_remaining <= 1
                      ? 'border-amber-500/40 bg-amber-950/30 text-amber-200'
                      : 'border-zinc-700/50 bg-zinc-900/40 text-zinc-400'
              }`}
              title={language === 'es'
                ? 'Plan Gratis — Actualiza a Premium para mensajes ilimitados'
                : 'Free Plan — Upgrade to Premium for unlimited messages'}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  userLimits.mode === 'blocked'
                    ? 'bg-rose-400'
                    : userLimits.mode === 'trial'
                      ? 'bg-emerald-400'
                      : userLimits.daily_messages_remaining <= 1
                        ? 'bg-amber-400'
                        : 'bg-zinc-500'
                }`}
              />
              {userLimits.mode === 'trial' ? (
                <span data-testid="chatinput-free-tier-counter-trial">
                  {language === 'es'
                    ? `Primer Mes Gratis — te quedan ${userLimits.trial_messages_remaining}/${userLimits.trial_messages_max} mensajes`
                    : `First Free Month — ${userLimits.trial_messages_remaining}/${userLimits.trial_messages_max} messages left`}
                </span>
              ) : userLimits.mode === 'blocked' ? (
                <span data-testid="chatinput-free-tier-counter-blocked">
                  {language === 'es'
                    ? `Plan Gratis — te quedan 0/${userLimits.daily_messages_max} mensajes hoy`
                    : `Free Plan — 0/${userLimits.daily_messages_max} messages left today`}
                </span>
              ) : (
                <span data-testid="chatinput-free-tier-counter-daily">
                  {language === 'es'
                    ? `Plan Gratis — te quedan ${userLimits.daily_messages_remaining}/${userLimits.daily_messages_max} mensajes hoy`
                    : `Free Plan — ${userLimits.daily_messages_remaining}/${userLimits.daily_messages_max} messages left today`}
                </span>
              )}
              <button
                type="button"
                onClick={navigateToPremium}
                data-testid="chatinput-free-tier-counter-upgrade"
                className="ml-1 px-1.5 py-[1px] rounded-full text-[9px] sm:text-[10px] font-semibold bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 hover:from-emerald-300 hover:to-cyan-300 transition-colors"
              >
                {language === 'es' ? 'Actualizar' : 'Upgrade'}
              </button>
            </div>
          </div>
        )}
      </form>
      
      {/* Microphone Help Modal */}
      <MicrophoneHelpModal />
    </div>
  );
};

export default ChatInput;
